import type { Editor } from "@tiptap/react";
import type { ReactElement } from "react";
import type { Asset } from "schemas/generated/asset";
import type {
  ChatAssetItem,
  ChatProductItem,
} from "schemas/generated/chatMessage";
import type {
  Product,
  SchemaReploShopifyProduct,
} from "schemas/generated/product";

import React, { forwardRef, useCallback, useRef, useState } from "react";

import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useAIStreaming } from "@editor/providers/AIStreamingProvider";

import {
  useIsChatLoading,
  useMessages,
} from "@/features/react-editor/components/chat/store/ChatStoreContext";
import EmptyModifierState from "@/features/react-editor/components/modifiers/base/EmptyModifier";
import Modifiers from "@/features/react-editor/components/modifiers/Modifiers";
import { useModifiersStore } from "@/features/react-editor/components/modifiers/store/ModifiersStoreContext";
import IconButton from "@replo/design-system/components/button/IconButton";
import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import twMerge from "@replo/design-system/utils/twMerge";
import { ArrowDown } from "lucide-react";

import { ContextItemsMenu } from "./ContextItemsMenu/ContextItemsMenu";
import { MessageInput } from "./MessageInput";
import { MessageList } from "./MessageList";
import { useAutoScroll } from "./use-auto-scroll";

interface ChatProps {
  handleSubmit: (e: React.FormEvent) => void;
  className?: string;
  ownerId?: string;
  stop?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  files: File[] | null;
  setFiles: React.Dispatch<React.SetStateAction<File[] | null>>;
  editor: Editor | null;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  sendChatRequest: ({
    prompt,
    isHidden,
  }: {
    prompt: string;
    isHidden: boolean;
  }) => Promise<void>;
  onForceScrollToTop?: React.MutableRefObject<(() => void) | null>;
}

export function Chat({
  ownerId,
  handleSubmit,
  stop,
  className,
  files,
  setFiles,
  editor,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  sendChatRequest,
  onForceScrollToTop,
}: ChatProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const { status } = useAIStreaming();
  const { user } = useCurrentUser();
  const isChatLoading = useIsChatLoading();
  const messages = useMessages();

  const lastMessage = messages.at(-1);
  const isTyping = lastMessage?.role === "user" && isChatLoading;
  const canEdit = !ownerId || ownerId === user?.id;

  const {
    displayMode,
    selectedElement,
    contextMenuOpen,
    actions: modifierActions,
  } = useModifiersStore();

  const handleContextItemSelect = (
    type: "asset" | "product",
    data: ChatAssetItem | ChatProductItem,
  ) => {
    if (!editor) {
      return;
    }

    const { state } = editor;
    const { selection } = state;
    const cursorPos = selection.anchor;

    // Find the @ character before the cursor
    let atPosition = -1;
    for (let i = cursorPos - 1; i >= 0; i--) {
      const char = state.doc.textBetween(i, i + 1);
      if (char === "@") {
        atPosition = i;
        break;
      }
      // Stop searching if we hit a space or start of document
      if (char === " " || i === 0) {
        break;
      }
    }

    // Create the appropriate HTML component
    const componentTag =
      type === "asset" ? "asset-component" : "product-component";
    const html = `<${componentTag} data="${JSON.stringify(data).replace(/"/g, "&quot;")}"></${componentTag}>`;

    if (atPosition !== -1) {
      // Replace the @ with the component
      editor
        .chain()
        .focus()
        .deleteRange({ from: atPosition, to: atPosition + 1 }) // Delete the @
        .insertContentAt(atPosition, html) // Insert component at @ position
        .run();
    } else {
      // Fallback: just insert the component if @ not found
      editor.chain().insertContent(html).focus().run();
    }

    modifierActions.setContextMenuOpen(false);
  };

  const handleAssetSelect = (asset: Asset) => {
    const chatAssetItemData: ChatAssetItem = {
      id: asset.id,
      name: asset.name,
      url: asset.url,
      fileExtension: asset.fileExtension,
    };

    handleContextItemSelect("asset", chatAssetItemData);
  };

  const handleProductSelect = (product: Product) => {
    const productItemData: ChatProductItem = {
      id: product.id,
      title: product.title,
      description: product.description ?? "",
      status: product.status,
      featuredMedia: product.featuredMedia,
      media: product.media,
      options: product.options,
      variants: product.variants,
      sellingPlanGroups: product.sellingPlanGroups,
    };

    handleContextItemSelect("product", productItemData);
  };

  return (
    <ChatContainer className={className}>
      <div className="relative flex min-h-0 h-full">
        <ChatMessages
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onForceScrollToTop={onForceScrollToTop}
        >
          <MessageList
            isTyping={isTyping}
            editor={editor}
            sendChatRequest={sendChatRequest}
          />
        </ChatMessages>

        {canEdit && displayMode === "select" && selectedElement && (
          <div className="absolute inset-0 flex items-end">
            <Modifiers />
          </div>
        )}
      </div>
      {canEdit && (
        <>
          {status === "generating" && (
            <div className="flex items-center gap-2 text-foreground mb-2">
              <img
                src="/images/templates/ai-loader.svg"
                alt="AI is thinking"
                className="w-6 h-6 animate-spin"
              />
              <span className="typ-body-base">Applying changes...</span>
            </div>
          )}
          {displayMode === "select" && !selectedElement && (
            <EmptyModifierState />
          )}

          <div className="relative">
            <ContextItemsMenu
              open={contextMenuOpen}
              formRef={formRef}
              onAssetSelect={handleAssetSelect}
              onProductSelect={handleProductSelect}
            />
            <form
              ref={formRef}
              onSubmit={(e) => {
                void handleSubmit(e);
              }}
              className="mt-3"
            >
              <div className="relative">
                <MessageInput
                  files={files}
                  setFiles={setFiles}
                  allowAttachments={true}
                  stop={stop}
                  enableInterrupt={true}
                  editor={editor}
                  isChatLoading={isChatLoading}
                  displayMode={displayMode}
                  toggleDisplayMode={modifierActions.toggleDisplayMode}
                  contextMenuOpen={contextMenuOpen}
                  setContextMenuOpen={modifierActions.setContextMenuOpen}
                />
              </div>
            </form>
          </div>
        </>
      )}
    </ChatContainer>
  );
}

function ChatMessages({
  children,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  onForceScrollToTop,
}: React.PropsWithChildren & {
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onForceScrollToTop?: React.MutableRefObject<(() => void) | null>;
}) {
  const messages = useMessages();
  const {
    containerRef,
    scrollToBottom,
    forceScrollToBottom,
    handleScroll,
    shouldAutoScroll,
    handleTouchStart,
  } = useAutoScroll([messages]);

  // Expose forceScrollToBottom function to parent
  React.useEffect(() => {
    if (onForceScrollToTop) {
      onForceScrollToTop.current = forceScrollToBottom;
    }
  }, [forceScrollToBottom, onForceScrollToTop]);

  // NOTE (Bobby: 2025-07-13): Handling a lot of the infinite scroll logic custom since the InfiniteScroll component
  // was not working inverse as wanted. Pagination wasn't working with the inverse order
  const previousScrollHeightRef = useRef<number | null>(null);
  const isLoadingMoreRef = useRef(false);

  const handleScrollWithInfiniteLoad = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      handleScroll();

      // Check if user has scrolled to the top and we have more messages to load
      const target = e.target as HTMLDivElement;
      const scrollTop = target.scrollTop;

      if (
        scrollTop === 0 &&
        hasNextPage &&
        !isFetchingNextPage &&
        fetchNextPage
      ) {
        // Store current scroll height before loading more messages
        previousScrollHeightRef.current = target.scrollHeight;
        isLoadingMoreRef.current = true;
        fetchNextPage();
      }
    },
    [handleScroll, hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  // Restore scroll position after new messages are loaded
  React.useEffect(() => {
    if (
      !isFetchingNextPage &&
      isLoadingMoreRef.current &&
      containerRef.current &&
      previousScrollHeightRef.current !== null
    ) {
      // Use requestAnimationFrame to ensure DOM is fully updated
      requestAnimationFrame(() => {
        if (containerRef.current && previousScrollHeightRef.current !== null) {
          const newScrollHeight = containerRef.current.scrollHeight;
          const heightDifference =
            newScrollHeight - previousScrollHeightRef.current;

          // Restore scroll position to maintain visual position
          containerRef.current.scrollTop = heightDifference;
          previousScrollHeightRef.current = null;
          isLoadingMoreRef.current = false;
        }
      });
    }
  }, [isFetchingNextPage, containerRef]);

  return (
    <div
      className="grid grid-cols-1 overflow-y-auto pb-6 [scrollbar-width:none] h-full min-h-0"
      ref={containerRef}
      onScroll={handleScrollWithInfiniteLoad}
      onTouchStart={handleTouchStart}
    >
      <div className="max-w-full [grid-column:1/1] [grid-row:1/1] h-full min-h-0">
        {isFetchingNextPage && (
          <div className="flex items-center justify-center py-2">
            <Spinner variant="secondary" size={16} />
          </div>
        )}
        {children}
      </div>

      <div className="flex flex-1 items-end justify-end [grid-column:1/1] [grid-row:1/1]">
        {!shouldAutoScroll && (
          <div className="sticky bottom-0 left-0 flex w-full justify-end">
            <IconButton
              variant="tertiary"
              onClick={scrollToBottom}
              icon={<ArrowDown className="h-4 w-4" />}
              layoutClassName="h-8 w-8"
            />
          </div>
        )}
      </div>
    </div>
  );
}

const ChatContainer = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={twMerge(
        "grid max-h-full w-full grid-rows-[1fr_auto] h-full overflow-y-hidden min-h-0",
        className,
      )}
      {...props}
    />
  );
});
ChatContainer.displayName = "ChatContainer";

interface ChatFormProps {
  className?: string;
  isPending: boolean;
  handleSubmit: (
    event?: { preventDefault?: () => void },
    options?: {
      experimental_attachments?: FileList;
      selectedProduct?: SchemaReploShopifyProduct | null;
    },
  ) => void;
  children: (props: {
    files: File[] | null;
    setFiles: React.Dispatch<React.SetStateAction<File[] | null>>;
  }) => ReactElement;
}

const ChatForm = forwardRef<HTMLFormElement, ChatFormProps>(
  ({ children, handleSubmit, className }, ref) => {
    const [files, setFiles] = useState<File[] | null>(null);

    const onSubmit = (event: React.FormEvent) => {
      event.preventDefault();

      if (!files) {
        handleSubmit(event);
        return;
      }

      const fileList = createFileList(files);
      handleSubmit(event, { experimental_attachments: fileList });
      setFiles(null);
    };

    return (
      <form ref={ref} onSubmit={onSubmit} className={className}>
        {children({ files, setFiles })}
      </form>
    );
  },
);
ChatForm.displayName = "ChatForm";

function createFileList(files: File[] | FileList): FileList {
  const dataTransfer = new DataTransfer();
  for (const file of Array.from(files)) {
    dataTransfer.items.add(file);
  }
  return dataTransfer.files;
}
